/* ==============================================================================
   Cabeçalho da conversa direta
   ==============================================================================
   Quem está do outro lado, à esquerda; o que se pode fazer com essa pessoa, à
   direita.

   Ele existe SEPARADO da barra superior de propósito, e o motivo é o único que
   justifica duplicar um cabeçalho: a barra superior é da JANELA — ela mostra
   quem eu sou, o painel de administração, a marca. Este é da CONVERSA, e vive
   dentro do painel dela, alinhado com o cabeçalho "Amigos" da coluna ao lado.

   A diferença fica visível quando a conversa muda: este cabeçalho troca inteiro
   (outra pessoa, outro estado, outra chamada), e a barra da janela não muda
   nada. Enfiar os dois na mesma linha faria "Jhonatas Ortega" e "Luna"
   aparecerem lado a lado sem dizer qual é o dono da sessão e qual é o
   interlocutor — que foi exatamente como estava antes.

   Três linhas de identidade e não uma: nome, `@usuário` e estado.

     nome       é como a pessoa se chama, e muda quando ela quer
     @usuário   é como se ENDEREÇA ela, e não muda
     estado     é se vale a pena ligar agora

   O nome sozinho não permite adicionar de volta nem mencionar, e é o que se lê
   primeiro. O `@usuário` é o que se copia. Mostrar só um dos dois obriga a abrir
   o perfil para descobrir o outro.
============================================================================== */

import type { ReactNode } from 'react';
import { Avatar } from '../components/Avatar.tsx';

export type ConversationStatus = 'online' | 'idle' | 'dnd' | 'offline';

const STATUS_LABEL: Record<ConversationStatus, string> = {
  online: 'Online',
  idle: 'Ausente',
  dnd: 'Não perturbe',
  offline: 'Offline',
};

const STATUS_COLOR: Record<ConversationStatus, string> = {
  online: 'var(--color-success)',
  idle: 'var(--color-warning)',
  dnd: 'var(--color-danger)',
  offline: 'var(--color-text-tertiary)',
};

export interface ConversationHeaderProps {
  /** Nome exibido: apelido, nome de exibição ou nome de usuário. */
  name: string;
  /** Semente do avatar. O ID da pessoa, para o gradiente ser sempre o mesmo. */
  seed: string;
  /** Sem o `@` — ele é desenhado aqui. Ausente quando a conversa não é com uma pessoa. */
  username?: string | undefined;
  status?: ConversationStatus | undefined;
  /** Botões de chamada, à direita. */
  actions?: ReactNode;
}

export function ConversationHeader({
  name,
  seed,
  username,
  status = 'offline',
  actions,
}: ConversationHeaderProps) {
  return (
    <div
      className="flex shrink-0 items-center justify-between gap-3 px-4"
      style={{
        minHeight: 56,
        /*
         * A borda fica embaixo e não em volta: o cabeçalho é o TOPO do painel da
         * conversa, não um cartão dentro dele. Uma borda completa criaria uma
         * moldura que a lista de mensagens teria que respeitar, e a lista rola —
         * o conteúdo passaria por baixo de uma linha que sugere limite.
         */
        borderBottom: '1px solid var(--color-border-subtle)',
      }}
    >
      <div className="flex min-w-0 items-center gap-2.5">
        <Avatar name={name} seed={seed} size={40} status={status} />
        <div className="min-w-0">
          <h1
            className="truncate font-semibold"
            style={{
              fontSize: 'var(--text-lg)',
              letterSpacing: 'var(--tracking-tight)',
              color: 'var(--color-text-primary)',
            }}
          >
            {name}
          </h1>
          {/*
            `@usuário` e estado na MESMA linha, separados por espaço.

            Empilhados seriam três linhas de metadado sob um nome de 18px, e o
            cabeçalho passaria de 56 para 76px — altura tirada da conversa, que é
            o conteúdo. Lado a lado eles cabem porque são ambos curtos e ambos
            secundários: nenhum dos dois ganha da leitura do outro.
          */}
          <div
            className="flex min-w-0 items-center gap-2"
            style={{ fontSize: 'var(--text-2xs)' }}
          >
            {username !== undefined && (
              <span className="truncate" style={{ color: 'var(--color-text-tertiary)' }}>
                @{username}
              </span>
            )}
            <span className="flex shrink-0 items-center gap-1">
              <span
                aria-hidden="true"
                className="block rounded-full"
                style={{
                  width: 7,
                  height: 7,
                  background: STATUS_COLOR[status],
                }}
              />
              <span style={{ color: 'var(--color-text-tertiary)' }}>
                {STATUS_LABEL[status]}
              </span>
            </span>
          </div>
        </div>
      </div>

      {actions && <div className="flex shrink-0 items-center">{actions}</div>}
    </div>
  );
}
